"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { useUser } from "@clerk/nextjs";
import { History } from "lucide-react";
import { Button } from "@/components/ui/button";
import PreviousSessionDialog from "@/components/PreviousSessionDialog";
import {
  BadgeId,
  UserBadge,
  BadgeDefinition,
  BADGES,
  BADGE_CATEGORIES,
  RARITY_STYLES,
  BadgeCategory,
} from "@/types/badge";
import { getUserBadges } from "@/lib/server-actions/badgeActions";

const SIZE_CLASSES: Record<"sm" | "md" | "lg", string> = {
  sm: "w-12 h-12",
  md: "w-24 h-24",
  lg: "w-32 h-32",
};
const SIZE_PX: Record<"sm" | "md" | "lg", number> = { sm: 48, md: 96, lg: 128 };
const RARITY_MEDAL_CLASS: Record<BadgeDefinition["rarity"], string> = {
  common: "badge-medal--common",
  uncommon: "badge-medal--uncommon",
  rare: "badge-medal--rare",
  epic: "badge-medal--epic",
  legendary: "badge-medal--legendary",
};
const CAT_MEDAL_CLASS: Record<BadgeCategory, string> = {
  problem: "badge-medal--hex",
  subject: "badge-medal--shield",
  plan: "badge-medal--rosette",
  attendance: "badge-medal--circle",
  completion: "badge-medal--rosette", // 기출 완주 — 리본(rosette) 형태(JCG-2262)
};

function MedalVisual({ definition, size }: { definition: BadgeDefinition; size: "sm" | "md" | "lg" }) {
  return (
    <span className="badge-medal__shape">
      <span className="badge-medal__ring" aria-hidden="true" />
      <span className="badge-medal__core">
        {definition.image ? (
          <span className="badge-medal__imgwrap">
            <Image src={definition.image} alt="" fill sizes={`${SIZE_PX[size]}px`} className="object-contain" draggable={false} />
          </span>
        ) : (
          <span className="badge-medal__emoji" role="img" aria-label={definition.name}>{definition.icon}</span>
        )}
        <span className="badge-medal__holo" aria-hidden="true" />
        <span className="badge-medal__gloss" aria-hidden="true" />
      </span>
    </span>
  );
}

// 정적 배지 메달 (클릭/선택 없는 표시용) — 정보 패널·획득 목록·이전 회차 다이얼로그 공용
export function StaticBadgeMedal({
  badgeId,
  size = "sm",
  boxClassName = "w-12 h-12",
}: {
  badgeId: BadgeId;
  size?: "sm" | "md" | "lg";
  boxClassName?: string;
}) {
  const definition = BADGES[badgeId];
  if (!definition) return null;
  return (
    <div
      className={`badge-medal badge-medal--static ${CAT_MEDAL_CLASS[definition.category]} ${RARITY_MEDAL_CLASS[definition.rarity]} ${boxClassName} flex-shrink-0`}
    >
      <MedalVisual definition={definition} size={size} />
    </div>
  );
}

// 선택된 배지 정보 타입
interface SelectedBadgeInfo {
  badgeId: BadgeId;
  isEarned: boolean;
  earnedAt?: number;
}

interface BadgeDisplayProps {
  badge: UserBadge;
  size?: "sm" | "md" | "lg";
  onSelect?: (info: SelectedBadgeInfo) => void;
  isSelected?: boolean;
}

// 단일 배지 표시 컴포넌트
export function BadgeDisplay({ badge, size = "md", onSelect, isSelected }: BadgeDisplayProps) {
  const definition = BADGES[badge.badgeId];
  if (!definition) return null;

  return (
    <button
      type="button"
      onClick={() => onSelect?.({ badgeId: badge.badgeId, isEarned: true, earnedAt: badge.earnedAt })}
      aria-label={definition.name}
      title={definition.name}
      className={`badge-medal ${CAT_MEDAL_CLASS[definition.category]} ${RARITY_MEDAL_CLASS[definition.rarity]} ${SIZE_CLASSES[size]} ${isSelected ? "badge-medal--selected" : ""}`}
    >
      <MedalVisual definition={definition} size={size} />
    </button>
  );
}

// 미획득 배지 표시 컴포넌트
interface LockedBadgeProps {
  badgeId: BadgeId;
  size?: "sm" | "md" | "lg";
  onSelect?: (info: SelectedBadgeInfo) => void;
  isSelected?: boolean;
}

export function LockedBadge({ badgeId, size = "md", onSelect, isSelected }: LockedBadgeProps) {
  const definition = BADGES[badgeId];
  if (!definition) return null;

  return (
    <button
      type="button"
      onClick={() => onSelect?.({ badgeId, isEarned: false })}
      aria-label={`${definition.name} (미획득)`}
      title={`${definition.name} (미획득)`}
      className={`badge-medal badge-medal--locked ${CAT_MEDAL_CLASS[definition.category]} ${RARITY_MEDAL_CLASS[definition.rarity]} ${SIZE_CLASSES[size]} ${isSelected ? "badge-medal--selected" : ""}`}
    >
      <MedalVisual definition={definition} size={size} />
    </button>
  );
}

// 배지 그리드 (카테고리별)
interface BadgeGridProps {
  earnedBadges: UserBadge[];
  category?: BadgeCategory;
  showLocked?: boolean;
  onSelectBadge?: (info: SelectedBadgeInfo) => void;
  selectedBadgeId?: BadgeId | null;
}

export function BadgeGrid({ earnedBadges, category, showLocked = true, onSelectBadge, selectedBadgeId }: BadgeGridProps) {
  const allBadges = Object.values(BADGES).filter(
    (badge) => !category || badge.category === category
  );

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 sm:gap-5 justify-items-center">
      {allBadges.map((badge) => {
        const earned = earnedBadges.find((b) => b.badgeId === badge.id);
        if (earned) {
          return (
            <BadgeDisplay
              key={badge.id}
              badge={earned}
              size="md"
              onSelect={onSelectBadge}
              isSelected={selectedBadgeId === badge.id}
            />
          );
        }
        if (showLocked) {
          return (
            <LockedBadge
              key={badge.id}
              badgeId={badge.id}
              size="md"
              onSelect={onSelectBadge}
              isSelected={selectedBadgeId === badge.id}
            />
          );
        }
        return null;
      })}
    </div>
  );
}

// 도전과제 페이지 (/my/badges 라우트에서 사용)
export type BadgeCategoryFilter = BadgeCategory | "all";

/**
 * @param selectedCategory/onCategoryChange 카테고리 필터를 우측 사이드바(BadgesAside)로 이전하기
 *   위한 제어형 props(JCG-2126). 라우트가 카테고리 상태를 소유해 aside·본문이 공유한다.
 *   미지정 시 내부 state 로 폴백.
 */
export function BadgesPage({
  selectedCategory: controlledCategory,
  onCategoryChange,
}: {
  selectedCategory?: BadgeCategoryFilter;
  onCategoryChange?: (c: BadgeCategoryFilter) => void;
} = {}) {
  const { user } = useUser();
  const [badges, setBadges] = useState<UserBadge[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [internalCategory, setInternalCategory] =
    useState<BadgeCategoryFilter>("all");
  const selectedCategory = controlledCategory ?? internalCategory;
  const setSelectedCategory = (c: BadgeCategoryFilter) => {
    if (onCategoryChange) onCategoryChange(c);
    else setInternalCategory(c);
  };
  const [selectedBadge, setSelectedBadge] = useState<SelectedBadgeInfo | null>(null);

  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      try {
        const badgeData = await getUserBadges();
        if (badgeData) {
          setBadges(badgeData.badges);
        }
      } catch (error) {
        console.error("배지 데이터 로드 오류:", error);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  const earnedCount = badges.length;
  const totalCount = Object.keys(BADGES).length;

  return (
    <div className="space-y-6 p-4">
      {/* 헤더 */}
      <div className="relative flex flex-col items-center gap-3 text-center sm:block">
        <h2 className="text-xl font-bold">도전과제</h2>
        <p className="text-sm text-muted-foreground mt-1">
          획득한 배지: {earnedCount} / {totalCount}
        </p>
        {user?.id && (
          <div className="sm:absolute sm:right-0 sm:top-0">
            <PreviousSessionDialog
              mode="badges"
              userId={user.id}
              trigger={
                <Button variant="outline" size="sm">
                  <History className="h-3.5 w-3.5 mr-1" />
                  이전 회차
                </Button>
              }
            />
          </div>
        )}
      </div>

      {/* 카테고리 필터 — 제어형(우측 사이드바에서 전환, JCG-2126)일 때는 본문에서 숨기고,
          비제어형(독립 사용)일 때만 본문에 노출한다. */}
      {!controlledCategory && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSelectedCategory("all")}
            className={`min-h-8 rounded-full px-3 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
              selectedCategory === "all"
                ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                : "bg-gray-100 text-foreground dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
          >
            전체
          </button>
          {(Object.keys(BADGE_CATEGORIES) as BadgeCategory[]).map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory(cat)}
              className={`min-h-8 rounded-full px-3 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
                selectedCategory === cat
                  ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                  : "bg-gray-100 text-foreground dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              {BADGE_CATEGORIES[cat].name}
            </button>
          ))}
        </div>
      )}

      {/* 선택된 배지 정보 */}
      {selectedBadge && (
        <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          {(() => {
            const definition = BADGES[selectedBadge.badgeId];
            if (!definition) return null;
            return (
              <div className="flex items-start gap-3">
                <StaticBadgeMedal badgeId={definition.id} size="md" boxClassName="w-16 h-16" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">
                    {selectedBadge.isEarned ? definition.name : "???"}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {definition.description}
                  </div>
                  {selectedBadge.isEarned && selectedBadge.earnedAt && (
                    <div className="text-xs text-muted-foreground mt-2">
                      획득: {new Date(selectedBadge.earnedAt).toLocaleDateString("ko-KR")}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedBadge(null)}
                  aria-label="닫기"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-accent hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
            );
          })()}
        </div>
      )}

      {/* 배지 그리드 */}
      <BadgeGrid
        earnedBadges={badges}
        category={selectedCategory === "all" ? undefined : selectedCategory}
        showLocked={true}
        onSelectBadge={setSelectedBadge}
        selectedBadgeId={selectedBadge?.badgeId}
      />

      {/* 획득한 배지 목록 */}
      {badges.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-sm">획득한 배지</h3>
          <div className="space-y-2">
            {badges
              .sort((a, b) => b.earnedAt - a.earnedAt)
              .slice(0, 5)
              .map((badge) => {
                const definition = BADGES[badge.badgeId];
                if (!definition) return null;
                const rarityStyle = RARITY_STYLES[definition.rarity];
                return (
                  <div
                    key={badge.badgeId}
                    className={`flex items-center gap-3 p-2 rounded-lg ${rarityStyle.bg} border ${rarityStyle.border}`}
                  >
                    <StaticBadgeMedal badgeId={definition.id} size="sm" boxClassName="w-12 h-12" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">
                        {definition.name}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {definition.description}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(badge.earnedAt).toLocaleDateString("ko-KR")}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

// 배지 요약 (간단한 표시용)
interface BadgeSummaryProps {
  maxDisplay?: number;
  /**
   * 지난 회차 스냅샷(JCG-3283) 주입 배지. 있으면 self-fetch(getUserBadges, award-on-read
   * 부작용)를 완전히 스킵하고 주입값을 렌더한다.
   */
  badges?: UserBadge[];
}

export function BadgeSummary({
  maxDisplay = 5,
  badges: injectedBadges,
}: BadgeSummaryProps) {
  const [fetchedBadges, setFetchedBadges] = useState<UserBadge[]>([]);
  const [isLoading, setIsLoading] = useState(injectedBadges === undefined);

  useEffect(() => {
    if (injectedBadges !== undefined) return; // 주입 모드: fetch 스킵
    async function loadBadges() {
      try {
        const data = await getUserBadges();
        if (data) {
          setFetchedBadges(data.badges);
        }
      } catch (error) {
        console.error("배지 로드 오류:", error);
      } finally {
        setIsLoading(false);
      }
    }
    loadBadges();
  }, [injectedBadges]);

  const badges = injectedBadges ?? fetchedBadges;

  if (isLoading) {
    return <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />;
  }

  if (badges.length === 0) {
    return (
      <span className="text-sm text-muted-foreground">배지 없음</span>
    );
  }

  const displayBadges = badges
    .slice()
    .sort((a, b) => b.earnedAt - a.earnedAt)
    .slice(0, maxDisplay);

  const remainingCount = badges.length - maxDisplay;

  return (
    <div className="flex items-center gap-1">
      {displayBadges.map((badge) => (
        <BadgeDisplay key={badge.badgeId} badge={badge} size="sm" />
      ))}
      {remainingCount > 0 && (
        <span className="text-xs text-muted-foreground ml-1">
          +{remainingCount}
        </span>
      )}
    </div>
  );
}
